// BlockEditor — the heart of the editorial editor.
//
// Owns the BlockDocument state, renders the sortable list of blocks,
// and wires up DnD via @dnd-kit. Each block is rendered through
// BlockItem (which adds the gutter UI: drag handle, "+", toolbar).
//
// Insertion model:
//   • Between any two blocks (and at start/end) there is a "gap" — the
//     "+" button appears on hover and opens BlockMenu.
//   • Inside an empty paragraph, pressing "/" also opens BlockMenu at
//     that block's position (replacing the paragraph if user picks).
//
// Persistence: BlockEditor itself does NOT save — it bubbles every
// document change up to its parent (ArticleEditPage) via onChange.
// The parent is responsible for debounced auto-save.
//
// Selection: at most one block selected at any moment. Selection drives
// the floating BlockToolbar and the inline-format toolbar inside
// contentEditable text blocks.

import { useCallback, useState } from "react";
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";
import {
  type Block,
  type BlockDocument,
  type BlockType,
  makeBlock,
  validateBlock,
} from "../../lib/blocks";
import { BlockItem } from "./BlockItem";
import { BlockMenu } from "./BlockMenu";
import { t } from "./theme";

export interface BlockEditorProps {
  doc: BlockDocument;
  onChange: (next: BlockDocument) => void;
  articleId: string;          // for file uploads to PB
  mediaAssets: string[];      // already-uploaded files on the record
  onUploadFile: (file: File) => Promise<string>; // returns saved filename
}

export function BlockEditor({
  doc,
  onChange,
  articleId,
  mediaAssets,
  onUploadFile,
}: BlockEditorProps) {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  // Gap-menu open at index N means: insert at position N (0 = top).
  const [menuOpenAt, setMenuOpenAt] = useState<number | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const setBlocks = useCallback(
    (next: Block[]) => onChange({ ...doc, blocks: next }),
    [doc, onChange],
  );

  const updateBlock = useCallback(
    (id: string, patch: Partial<Block>) => {
      const next = doc.blocks.map((b) =>
        b.id === id ? ({ ...b, ...patch } as Block) : b,
      );
      setBlocks(next);
    },
    [doc.blocks, setBlocks],
  );

  const insertBlock = useCallback(
    (index: number, type: BlockType) => {
      const b = makeBlock(type);
      const next = [...doc.blocks];
      next.splice(index, 0, b);
      setBlocks(next);
      setSelectedId(b.id);
      setMenuOpenAt(null);
    },
    [doc.blocks, setBlocks],
  );

  const insertBlocks = useCallback(
    (index: number, blocks: Block[]) => {
      if (blocks.length === 0) return;
      const next = [...doc.blocks];
      next.splice(index, 0, ...blocks);
      setBlocks(next);
      setSelectedId(blocks[0].id);
      setMenuOpenAt(null);
    },
    [doc.blocks, setBlocks],
  );

  // Replace a block at `index` with a new one of `type`. Used by "/" slash
  // menu inside an empty paragraph — that paragraph swaps out for the new.
  const replaceBlock = useCallback(
    (index: number, type: BlockType) => {
      const b = makeBlock(type);
      const next = [...doc.blocks];
      next[index] = b;
      setBlocks(next);
      setSelectedId(b.id);
      setMenuOpenAt(null);
    },
    [doc.blocks, setBlocks],
  );

  // Magic-paste entry point. Called by ParagraphBlockEditor when the user
  // pastes a chunk of markdown / rich HTML that parsed into multiple
  // blocks. If the current paragraph is empty we REPLACE it; otherwise
  // we insert the new blocks immediately after.
  const pasteBlocksAt = useCallback(
    (currentId: string, newBlocks: Block[]) => {
      if (newBlocks.length === 0) return;
      const idx = doc.blocks.findIndex((b) => b.id === currentId);
      if (idx < 0) return;
      const current = doc.blocks[idx];
      const isEmptyParagraph =
        current.type === "paragraph" &&
        !(current.html || "").replace(/<[^>]*>/g, "").trim();
      const next = [...doc.blocks];
      if (isEmptyParagraph) {
        next.splice(idx, 1, ...newBlocks);
      } else {
        next.splice(idx + 1, 0, ...newBlocks);
      }
      setBlocks(next);
      setSelectedId(newBlocks[newBlocks.length - 1].id);
    },
    [doc.blocks, setBlocks],
  );

  const removeBlock = useCallback(
    (id: string) => {
      const next = doc.blocks.filter((b) => b.id !== id);
      setBlocks(next);
      if (selectedId === id) setSelectedId(null);
    },
    [doc.blocks, setBlocks, selectedId],
  );

  const duplicateBlock = useCallback(
    (id: string) => {
      const idx = doc.blocks.findIndex((b) => b.id === id);
      if (idx < 0) return;
      const src = doc.blocks[idx];
      const copy: Block = { ...src, id: makeBlock(src.type).id };
      const next = [...doc.blocks];
      next.splice(idx + 1, 0, copy);
      setBlocks(next);
      setSelectedId(copy.id);
    },
    [doc.blocks, setBlocks],
  );

  const moveBlock = useCallback(
    (id: string, dir: -1 | 1) => {
      const idx = doc.blocks.findIndex((b) => b.id === id);
      if (idx < 0) return;
      const newIdx = idx + dir;
      if (newIdx < 0 || newIdx >= doc.blocks.length) return;
      setBlocks(arrayMove(doc.blocks, idx, newIdx));
    },
    [doc.blocks, setBlocks],
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      const oldIdx = doc.blocks.findIndex((b) => b.id === active.id);
      const newIdx = doc.blocks.findIndex((b) => b.id === over.id);
      if (oldIdx < 0 || newIdx < 0) return;
      setBlocks(arrayMove(doc.blocks, oldIdx, newIdx));
    },
    [doc.blocks, setBlocks],
  );

  // Empty state.
  if (doc.blocks.length === 0) {
    return (
      <div style={{ position: "relative", padding: "48px 0" }}>
        <button
          type="button"
          onClick={() => setMenuOpenAt(0)}
          style={{
            display: "block",
            width: "100%",
            padding: "40px 24px",
            background: t.card,
            border: `1px dashed ${t.border}`,
            borderRadius: 8,
            color: t.text3,
            fontSize: 14,
            textAlign: "center",
            transition: t.transition,
          }}
          onMouseEnter={(e) => {
            (e.currentTarget as HTMLButtonElement).style.borderColor =
              t.accentStrong;
            (e.currentTarget as HTMLButtonElement).style.color = t.accent;
          }}
          onMouseLeave={(e) => {
            (e.currentTarget as HTMLButtonElement).style.borderColor =
              t.border;
            (e.currentTarget as HTMLButtonElement).style.color = t.text3;
          }}
        >
          Start writing… or press + to add a block
        </button>
        {menuOpenAt === 0 && (
          <BlockMenu
            onPick={(type) => insertBlock(0, type)}
            onPickTemplate={(blocks) => insertBlocks(0, blocks)}
            onClose={() => setMenuOpenAt(null)}
          />
        )}
      </div>
    );
  }

  return (
    <div style={{ position: "relative" }}>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={doc.blocks.map((b) => b.id)}
          strategy={verticalListSortingStrategy}
        >
          {/* Gap before first block. */}
          <BlockGap
            index={0}
            isMenuOpen={menuOpenAt === 0}
            onOpen={() => setMenuOpenAt(0)}
            onClose={() => setMenuOpenAt(null)}
            onPick={(type) => insertBlock(0, type)}
            onPickTemplate={(blocks) => insertBlocks(0, blocks)}
          />
          {doc.blocks.map((block, idx) => (
            <div key={block.id}>
              <BlockItem
                block={block}
                index={idx}
                total={doc.blocks.length}
                isSelected={selectedId === block.id}
                onSelect={() => setSelectedId(block.id)}
                onChange={(patch) => updateBlock(block.id, patch)}
                onRemove={() => removeBlock(block.id)}
                onDuplicate={() => duplicateBlock(block.id)}
                onMove={(dir) => moveBlock(block.id, dir)}
                onSlashMenu={() => setMenuOpenAt(idx)}
                onInsertAfter={(type) => insertBlock(idx + 1, type)}
                replaceSelf={(type) => replaceBlock(idx, type)}
                onPasteBlocks={(blocks) => pasteBlocksAt(block.id, blocks)}
                validationMsg={validateBlock(block)}
                articleId={articleId}
                mediaAssets={mediaAssets}
                onUploadFile={onUploadFile}
              />
              <BlockGap
                index={idx + 1}
                isMenuOpen={menuOpenAt === idx + 1}
                onOpen={() => setMenuOpenAt(idx + 1)}
                onClose={() => setMenuOpenAt(null)}
                onPick={(type) => insertBlock(idx + 1, type)}
                onPickTemplate={(blocks) => insertBlocks(idx + 1, blocks)}
              />
            </div>
          ))}
        </SortableContext>
      </DndContext>
    </div>
  );
}

// ── Gap (between-block insertion control) ────────────────────────────────

function BlockGap({
  isMenuOpen,
  onOpen,
  onClose,
  onPick,
  onPickTemplate,
}: {
  index: number;
  isMenuOpen: boolean;
  onOpen: () => void;
  onClose: () => void;
  onPick: (type: BlockType) => void;
  onPickTemplate: (blocks: Block[]) => void;
}) {
  const [hover, setHover] = useState(false);
  return (
    <div
      style={{
        position: "relative",
        height: hover || isMenuOpen ? 28 : 12,
        transition: "height 200ms ease",
      }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      {(hover || isMenuOpen) && (
        <button
          type="button"
          onClick={onOpen}
          aria-label="Insert block"
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 24,
            height: 24,
            borderRadius: 100,
            border: `1px solid ${t.border}`,
            background: t.card,
            color: t.text3,
            fontSize: 16,
            lineHeight: 1,
            padding: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            transition: t.transition,
            boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
          }}
          onMouseEnter={(e) => {
            (e.currentTarget as HTMLButtonElement).style.color = t.accent;
            (e.currentTarget as HTMLButtonElement).style.borderColor =
              t.accentStrong;
          }}
          onMouseLeave={(e) => {
            (e.currentTarget as HTMLButtonElement).style.color = t.text3;
            (e.currentTarget as HTMLButtonElement).style.borderColor =
              t.border;
          }}
        >
          +
        </button>
      )}
      {isMenuOpen && (
        <div
          style={{
            position: "absolute",
            top: "100%",
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 50,
          }}
        >
          <BlockMenu onPick={onPick} onPickTemplate={onPickTemplate} onClose={onClose} />
        </div>
      )}
    </div>
  );
}
